import { Checklist } from "@/bryntum-lib/models/Checklist";
import { Badge } from "@/modern/components/ui/badge";
import { Button } from "@/modern/components/ui/button-shadcn";
import { ButtonGroup } from "@/modern/components/ui/button-group";
import { CardTitle } from "@/modern/components/ui/card";
import { Dialog, DialogTrigger } from "@/modern/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { DashboardCard } from "@/modern/components/dashboard-card";
import { ModalItem, SharedModalContent, SharedModal } from "@/modern/components/shared-modal-component";
import { PinnedChecklistsProvider, usePinnedChecklists } from "@/react/hooks/usePinnedChecklists";
import { EyeIcon, ListTodoIcon, Plus } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useMemo, useState } from "react";
import { ChecklistAddItemsModal } from "@/react/components/entityViews/checklist/components/editors/ChecklistAddItemsModal";
import { ChecklistDetailModal } from "@/react/components/entityViews/checklist/components/editors/ChecklistDetailModal";
import { ChecklistDetailView } from "@/react/components/entityViews/checklist/components/ChecklistDetailView";
import { ChecklistRowComponent } from "@/react/components/entityViews/checklist/components/ChecklistRow";
import { EntityChecklistProvider, useEntityChecklistContext } from "@/react/components/entityViews/checklist/context/EntityChecklistContext";

const EmptyChecklistsState = () => {
  const { entityRecord } = useEntityChecklistContext();
  const createNewChecklist = (): Checklist => {
    const newChecklist = new Checklist();
    newChecklist.title = "";
    newChecklist[entityRecord.entityType] = entityRecord.id;
    return newChecklist;
  };

  return (
    <div className="flex flex-1 items-center justify-center h-full">
      <div className="h-full w-full">
        <div className="h-full flex flex-col items-center gap-6 rounded-2xl border border-dashed border-muted-foreground/30 bg-muted/40 px-8 py-8 text-center shadow-sm">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <ListTodoIcon className="h-6 w-6" />
          </span>
          <div className="space-y-2">
            <h3 className="text-base font-semibold text-foreground">No checklists yet</h3>
            <p className="text-sm text-muted-foreground">Create a checklist to track tasks, approvals, or punch items for this record.</p>
          </div>
          <Dialog>
            <DialogTrigger asChild>
              <Button>
                <Plus />
                Create Checklist
              </Button>
            </DialogTrigger>
            <SharedModalContent className="max-w-2xl">
              <ModalItem variant="title" modalTitle="Create new checklist">
                <ChecklistAddItemsModal checklist={createNewChecklist()} />
              </ModalItem>
            </SharedModalContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

const Header = ({
  onChecklistCreated: _onChecklistCreated,
  checklists,
  checklist,
  deselectChecklist
}: {
  onChecklistCreated?: (checklist: Checklist) => void;
  checklists: Checklist[];
  checklist: Checklist | null;
  deselectChecklist: () => void;
}) => {
  const { entityRecord } = useEntityChecklistContext();

  const createNewChecklist = (): Checklist => {
    const newChecklist = new Checklist();
    newChecklist.title = "";
    newChecklist[entityRecord.entityType] = entityRecord.id;
    return newChecklist;
  };

  return (
    <div className="flex justify-between items-center gap-2 flex-1">
      <CardTitle className="text-base relative">
        Checklists
        {checklists.length > 0 && (
          <Badge variant="muted" size="xs" className="absolute -right-7 -top-2">
            {checklists.length}
          </Badge>
        )}
      </CardTitle>
      <ButtonGroup>
        <Dialog>
          <AnimatePresence>
            {checklist && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <DialogTrigger asChild>
                    <Button variant="entityHeader" size="icon" asChild>
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3, type: "spring", bounce: 0 }}>
                        <EyeIcon />
                      </motion.div>
                    </Button>
                  </DialogTrigger>
                </TooltipTrigger>
                <TooltipContent>View checklist</TooltipContent>
              </Tooltip>
            )}
          </AnimatePresence>
          <ChecklistDetailModal checklist={checklist} onClose={deselectChecklist} />
        </Dialog>
        <Dialog>
          <Tooltip>
            <TooltipTrigger asChild>
              <DialogTrigger asChild>
                <Button variant="entityHeader" size="icon">
                  <Plus />
                </Button>
              </DialogTrigger>
            </TooltipTrigger>
            <TooltipContent>
              <p>Create new checklist</p>
            </TooltipContent>
          </Tooltip>
          <SharedModal className="md:max-w-2xl h-auto max-h-[90dvh]">
            <ModalItem variant="title" modalTitle="Create new checklist">
              <ChecklistAddItemsModal checklist={createNewChecklist()} />
            </ModalItem>
          </SharedModal>
        </Dialog>
      </ButtonGroup>
    </div>
  );
};

export const EntityChecklistView = () => {
  return (
    <EntityChecklistProvider>
      <PinnedChecklistsProvider>
        <EntityChecklistViewContent />
      </PinnedChecklistsProvider>
    </EntityChecklistProvider>
  );
};

const EntityChecklistViewContent = () => {
  const { checklists } = useEntityChecklistContext();
  const { pinnedChecklistIds } = usePinnedChecklists();

  // Calculate initial pinned checklist - derived state, no useEffect
  const initialPinnedChecklist = useMemo(() => {
    return checklists.find((checklist) => pinnedChecklistIds.includes(+checklist.id)) || null;
  }, []);

  const [selectedChecklist, setSelectedChecklist] = useState<Checklist | null>(initialPinnedChecklist);

  const handleChecklistClick = useCallback((checklist: Checklist | null) => {
    setSelectedChecklist(checklist);
  }, []);

  const handleChecklistCreated = useCallback((checklist: Checklist) => {
    // Navigate to the newly created checklist so user can add items
    setSelectedChecklist(checklist);
  }, []);

  const handleListView = useCallback(() => {
    setSelectedChecklist(null);
  }, []);
  return (
    <DashboardCard
      cardContentClassName={` px-0 min-w-0  ${selectedChecklist ? "overflow-hidden flex flex-col pt-4" : " overflow-y-scroll overflow-x-hidden py-4 "}`}
      headerClassName=" "
      header={<Header checklist={selectedChecklist} deselectChecklist={handleListView} checklists={checklists} onChecklistCreated={handleChecklistCreated} />}
    >
      <>
        <div className="flex-1 min-w-0 flex flex-col gap-6 min-h-0 h-full">
          {selectedChecklist ? (
            <ChecklistDetailView handleListView={handleListView} key="detail-view" checklist={selectedChecklist} onBack={handleListView} />
          ) : checklists.length === 0 ? (
            <div className="flex-1 px-4">
              <EmptyChecklistsState />
            </div>
          ) : (
            <div key="list-view" className="flex flex-col gap-4 flex-1 pl-4 pr-3 pb-10">
              {checklists.map((checklist) => (
                <motion.div key={checklist.id} layoutId={`checklist-${checklist.id}`}>
                  <ChecklistRowComponent checklist={checklist} onSelect={() => handleChecklistClick(checklist)} handleListView={handleListView} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </>
    </DashboardCard>
  );
};
